import { Icon, mergeStyleSets } from "@fluentui/react";
import { useFormikContext } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CheckboxField from "../../../../components/controls/CheckboxField";
import ComboBoxField from "../../../../components/controls/ComboBoxField";
import DatePickerField from "../../../../components/controls/DatePickerField";
import DoubleNumberInputField from "../../../../components/controls/DoubleNumberInputField";
import { TooltipComponent } from "../../../../components/TooltipComponent";
import { useSelector } from "../../../../store/hooks";
import { selectByIdLocations } from "../../../../store/locations/locations";
import useGetTaxonomyOptions from "../../../../utils/hooks/useGetTaxonomyOptions";
import AttachmentsFormField from "../../components/AttachmentsFormFields/AttachmentsFormField";
import { EditorField } from "../../components/EditorField";
import { useImplementationCostsMaxValueValidation } from "../../hooks/useMaxValueValidation";

function ResponseFields() {
  const { values, handleChange, errors, setFieldValue } =
    useFormikContext<any>();
  const { t } = useTranslation();
  const { locationId } = useParams() as any;

  useImplementationCostsMaxValueValidation();

  const location = useSelector((s) => selectByIdLocations(s, locationId));
  const clientId = location?.webBasePartnerNumber;
  const client = useSelector((s) =>
    clientId ? s.clients.entities[clientId] : null,
  );

  const costEstimateLimitsTooltip = client ? (
    <div>
      <div>
        {t("ram.clientsList.mediumLowThreshold")}:{" "}
        {client.mediumCostEstimateMin ?? "-"}
      </div>
      <div>
        {t("ram.clientsList.highMediumThreshold")}:{" "}
        {client.highCostEstimateMin ?? "-"}
      </div>
    </div>
  ) : (
    t("ram.costEstimate.noClientData")
  );

  const statusCompanyCodeOptions = useGetTaxonomyOptions({
    taxonomyKey: "RiskImplementationStatus",
    exclude: [
      "RiskImplementationStatus.DONE_CHECKED",
      "RiskImplementationStatus.HELD_IN_ABEYENCE",
    ],
  });

  const priorityCompanyCodeOptions = useGetTaxonomyOptions({
    taxonomyKey: "RiskPriority",
    include: [
      "RiskPriority.MINOR",
      "RiskPriority.MODERATE",
      "RiskPriority.MAJOR",
    ],
    exclude: [],
  });

  const costEstimateCodeOptions = useGetTaxonomyOptions({
    taxonomyKey: "RiskReductionCost",
    sort: false,
  });

  return (
    <div
      className={classes.container}
      style={{ paddingBottom: values?.useReminders ? 27 : 100 }}
    >
      <div className={classes.grid}>
        <DatePickerField
          disabled={false}
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values?.dateOfResponse}
          name={"dateOfResponse"}
          label={"ram.responsesPage.dateOfResponse.label"}
          placeholder={"ram.selectDate"}
          required={true}
          maxDate={new Date()}
          confidentiality={null}
        />
        <DatePickerField
          disabled={false}
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values?.plannedImplementationDate}
          name={"plannedImplementationDate"}
          label={"ram.responsesPage.plannedimplementation.label"}
          placeholder={"ram.selectDate"}
          required={false}
          confidentiality={null}
        />
        <ComboBoxField
          disabled={false}
          errors={errors}
          name="statusCompanyCode"
          label={"ram.responsesPage.statusCompanyCode.label"}
          setFieldValue={setFieldValue}
          options={statusCompanyCodeOptions || []}
          defaultValue={values?.statusCompanyCode}
          confidentiality={null}
          required
        />
        <ComboBoxField
          disabled={false}
          errors={errors}
          name="priorityCompanyCode"
          label={"ram.responsesPage.priorityCompanyCode.label"}
          setFieldValue={setFieldValue}
          options={priorityCompanyCodeOptions || []}
          defaultValue={values?.priorityCompanyCode}
          confidentiality={null}
          required={false}
        />
        <DoubleNumberInputField
          value={values?.implementationCostsEur}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          errors={errors}
          name={"implementationCostsEur"}
          disabled={false}
          required={false}
          label={"ram.responsesPage.implementationCostsEUR.label"}
        />

        <div className={classes.comboBoxWithInfo}>
          <ComboBoxField
            disabled={false}
            errors={errors}
            name="costEstimateCode"
            label={"ram.responsesPage.costEstimateCode.label"}
            setFieldValue={setFieldValue}
            options={costEstimateCodeOptions || []}
            defaultValue={values?.costEstimateCode}
            confidentiality={null}
            required={true}
          />
          <TooltipComponent content={costEstimateLimitsTooltip}>
            <Icon iconName="Info" className={classes.infoIcon} />
          </TooltipComponent>
        </div>
      </div>
      <div className={classes.fullWidth}>
        <AttachmentsFormField isDisabled={false} />
      </div>
      <div className={classes.fullWidth}>
        <EditorField
          name="comment"
          isRequired
          label="ram.responsesPage.comment.label"
        />
      </div>
      <div className={classes.grid}>
        <div className={classes.checkboxWithInfo}>
          <CheckboxField
            checked={values?.useReminders}
            disabled={false}
            label={"ram.responsesPage.useReminders.label"}
            name={"useReminders"}
            setFieldValue={(name, value) => {
              setFieldValue(name, value);
              if (!value) {
                setFieldValue("useSpecificReminderDate", false);
                setFieldValue("specificReminderDate", null);
              }
            }}
            errors={errors}
          />
          <TooltipComponent
            content={t(
              "ram.responsesPage.useSpecificReminderDate.automaticReminderInfo"
            )}
          >
            <Icon iconName="Info" className={classes.reminderInfoIcon} />
          </TooltipComponent>
        </div>
        <CheckboxField
          checked={values?.hideFromInsurer}
          disabled={false}
          label={"ram.responsesPage.displayedToInsurer.label"}
          name={"hideFromInsurer"}
          setFieldValue={setFieldValue}
          errors={errors}
        />
      </div>
      {values?.useReminders && (
        <div className={classes.reminderRow}>
          <CheckboxField
            checked={values?.useSpecificReminderDate}
            disabled={false}
            label={"ram.responsesPage.useSpecificReminderDate.label"}
            name={"useSpecificReminderDate"}
            setFieldValue={(name, value) => {
              setFieldValue(name, value);
              if (!value) {
                setFieldValue("specificReminderDate", null);
              }
            }}
            required={values?.useSpecificReminderDate}
            errors={errors}
          />
          <DatePickerField
            disabled={!values?.useSpecificReminderDate}
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values?.specificReminderDate}
            name={"specificReminderDate"}
            label={"ram.responsesPage.specificReminderDate.label"}
            placeholder={"ram.selectDate"}
            required={values?.useSpecificReminderDate}
            confidentiality={null}
            minDate={new Date()}
            hideLabel
          />
        </div>
      )}
    </div>
  );
}

export default ResponseFields;

const classes = mergeStyleSets({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  grid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: "20px",
    overfow: "auto",
  },
  reminderRow: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  fullWidth: {
    width: "100%",
  },
  comboBoxWithInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  infoIcon: {
    marginTop: 32,
    cursor: "pointer",
    color: "#605e5c",
  },
  reminderInfoIcon: {
    cursor: "pointer",
    color: "#605e5c",
  },
  checkboxWithInfo: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
  },
});
